import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(255),
  state: z.string().length(2).regex(/^[A-Z]{2}$/, "Invalid state code"),
});

export type JoinWaitlistResult =
  | { ok: true; duplicate: boolean; latencyMs: number }
  | { ok: false; error: string; latencyMs: number };

type DataLayerWindow = Window & { dataLayer?: Array<Record<string, unknown>> };

function track(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

export async function joinWaitlist(input: { email: string; state: string; placement?: string }): Promise<JoinWaitlistResult> {
  const start = performance.now();
  const placement = input.placement ?? "unknown";
  const stateCode = (input.state || "").toUpperCase();
  const is_pr = stateCode === "PR";

  // Funnel start — fired for every submit attempt, even invalid, so we can
  // measure form-start → success conversion per placement.
  track({ event: "waitlist_submit", state: stateCode, is_pr, placement });

  const parsed = schema.safeParse({ email: input.email, state: stateCode });
  if (!parsed.success) {
    const latencyMs = Math.round(performance.now() - start);
    const error = parsed.error.issues[0]?.message ?? "Invalid input";
    track({
      event: "waitlist_join_error",
      reason: "validation",
      error,
      state: stateCode,
      is_pr,
      placement,
      latency_ms: latencyMs,
    });
    return { ok: false, error, latencyMs };
  }

  const { error } = await supabase
    .from("waitlist")
    .insert({ email: parsed.data.email, state: parsed.data.state });

  const latencyMs = Math.round(performance.now() - start);
  const duplicate = error?.code === "23505";

  if (error && !duplicate) {
    track({
      event: "waitlist_join_error",
      reason: "server",
      error: error.code ?? "insert_failed",
      state: parsed.data.state,
      is_pr,
      placement,
      latency_ms: latencyMs,
    });
    return { ok: false, error: "Couldn't save your spot. Please try again.", latencyMs };
  }

  track({
    event: "waitlist_join",
    state: parsed.data.state,
    is_pr,
    status: duplicate ? "duplicate" : "new",
    placement,
    latency_ms: latencyMs,
  });
  return { ok: true, duplicate, latencyMs };
}

